import { useRef, useEffect, type ChangeEvent } from 'react';
import { Select } from './Input';
import styles from './Input.module.css';

interface SelectWithOtherProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[]; // must include "Other"
  placeholder?: string;
  required?: boolean;
}

const OTHER = 'Other';

/**
 * Strict Select dropdown that reveals an inline text input when "Other" is
 * selected — or when the current value doesn't match any predefined option.
 */
export function SelectWithOther({
  label,
  value,
  onChange,
  options,
  placeholder,
  required,
}: SelectWithOtherProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isEmpty = value === '';
  const isKnown = !isEmpty && value !== OTHER && options.includes(value);
  const showCustomInput = !isEmpty && !isKnown;
  // What the Select dropdown should display
  const selectValue = isEmpty ? '' : isKnown ? value : OTHER;
  // The text inside the custom input (empty when a known option is selected)
  const customText = isKnown || isEmpty ? '' : (value === OTHER ? '' : value);

  // Auto-focus the custom input when "Other" is first revealed
  useEffect(() => {
    if (showCustomInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCustomInput]);

  function handleSelectChange(e: ChangeEvent<HTMLSelectElement>) {
    const picked = e.target.value;
    // User picked a concrete option → emit it directly
    // User picked "Other" → emit "Other" (input will appear, they'll type)
    onChange(picked);
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const typed = e.target.value;
    // Emit the raw typed text so the form stores it (e.g. "Fairchild")
    // If they clear it, fall back to "Other" so the field isn't empty
    onChange(typed || OTHER);
  }

  return (
    <div className={styles.field}>
      <Select
        label={label}
        value={selectValue}
        onChange={handleSelectChange}
        required={required}
      >
        {isEmpty && <option value="">— Select —</option>}
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </Select>

      {showCustomInput && (
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          value={customText}
          onChange={handleInputChange}
          placeholder={placeholder}
          required={required}
        />
      )}
    </div>
  );
}
